import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import Text from 'src/components/Text';
import DataTable from './sub-component/Table';

import { useSummaryDetail } from './hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { CardContent, Box, Container, Grid, Typography, useTheme, IconButton, useMediaQuery } from '@mui/material';
import { useSummary } from '../hooks';
import { getActivityStyle } from 'src/util/activity-color';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { formatDateTime } from 'src/util/date-format';

interface SummaryDetailProps {
    isLoading: boolean;
}

function SummaryDetailMobile({ isLoading }: SummaryDetailProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');
    const
        {
            method:
            {
                setOpen,
                setDataItem,
                setDeleteOpen,
            }
        } = useSummaryDetail();

    const { data: { homeDetail, homeDetailGrid }, method: { fetchHomeDetail } } = useSummary();

    const handleDeleteOpen = (item) => {
        setDataItem(item);
        setDeleteOpen(true);
    };

    const handleBack = () => {
        navigate(-1);
    };

    const dataHome = useSelector((state: RootState) => state.Home.data.dataHome);

    useEffect(() => {
        fetchHomeDetail(dataHome?.booking_no);
    }, [dataHome]);

    const shipmentNumber = [
        { label: 'Shipment Number:', value: dataHome?.sn_number || '-' },
        { label: 'Booking No:', value: dataHome?.booking_no || '-' },
        { label: 'Reference No:', value: dataHome?.reference_no || '-' },
        { label: 'ETD – ETA Vessel:', value: dataHome?.etd_eta || '-' },
        { label: 'POL:', value: dataHome?.pol || '-' },
        { label: 'POD:', value: dataHome?.pod || '-' },
        { label: 'Last Activities Date:', value: dataHome?.last_activities_time ? formatDateTime(dataHome?.last_activities_time) : '-' },
    ];

    const bookingInformation = [
        { label: 'Consignee Name:', value: dataHome?.consignee_name || '-' },
        { label: 'Service Type:', value: homeDetail?.service_type || '-' },
        { label: 'Created By:', value: homeDetail?.created_by || '-' },
        { label: 'Created Date:', value: homeDetail?.created_date ? formatDateTime(homeDetail?.created_date) : '-' },
        { label: 'Sub Service Type:', value: homeDetail?.sub_service_type || '-' },
        { label: 'Vehicle Type:', value: homeDetail?.vehicle_type || '-' },
        // { label: 'Other Ref Doc Type 1:', value: dataItem?.other_ref_doc_type_1 || '-' },
    ];

    const senderDetails = [
        { label: 'Sender Ref. No:', value: homeDetail?.sender_ref_no || '-' },
        { label: 'Name:', value: homeDetail?.sender_name || '-' },
        { label: 'Company:', value: homeDetail?.sender_company || '-' },
        { label: 'Phone:', value: homeDetail?.sender_phone || '-' },
        { label: 'Address:', value: homeDetail?.sender_address || '-' },
        { label: 'City:', value: homeDetail?.sender_city || '-' },
        { label: 'Postal Code:', value: homeDetail?.sender_postal_code || '-' },
        { label: 'State:', value: homeDetail?.sender_state || '-' },
        { label: 'Country:', value: homeDetail?.sender_country || '-' },
        { label: 'Pick Up Date:', value: homeDetail?.pick_up_date ? formatDateTime(homeDetail?.pick_up_date) : '-' },
    ];

    const receiverDetails = [
        { label: 'Receiver Ref. No:', value: homeDetail?.receiver_ref_no || '-' },
        { label: 'Name:', value: homeDetail?.receiver_name || '-' },
        { label: 'Company:', value: homeDetail?.receiver_company || '-' },
        { label: 'Phone:', value: homeDetail?.receiver_phone || '-' },
        { label: 'Address:', value: homeDetail?.receiver_address || '-' },
        { label: 'City:', value: homeDetail?.receiver_city || '-' },
        { label: 'Postal Code:', value: homeDetail?.receiver_postal_code || '-' },
        { label: 'State:', value: homeDetail?.receiver_state || '-' },
        { label: 'Country:', value: homeDetail?.receiver_country || '-' },
        { label: 'Delivery Date:', value: homeDetail?.delivery_date ? formatDateTime(homeDetail?.delivery_date) : '-' },
    ];

    const shipmentInformation = [
        { label: 'Commodity:', value: homeDetail?.commodity || '-' },
        { label: 'Volume:', value: homeDetail?.volume || '-' },
        { label: 'Weight:', value: homeDetail?.weight || '-' },
        { label: 'Instruction Notes:', value: homeDetail?.instruction_notes || '-' },
        { label: 'BL Number:', value: homeDetail?.bl_number || '-' },
        { label: 'Serial Number:', value: '-' },
    ];



    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const renderDetails = useMemo(() => (details) => (
        <Grid container spacing={0}>
            {!isLoading && details.map((detail, index) => (
                <Grid item xs={6} sm={4} md={12} textAlign={{ sm: 'left' }} key={index}>
                    <React.Fragment >
                        <Box pr={3} pb={2}>
                            <Box sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                                {detail.label}
                            </Box>
                            <Text>
                                <b style={{ color: theme.colors.ckb.primary, fontSize: '12px' }}>{detail.value}</b>
                            </Text>
                        </Box>
                    </React.Fragment>
                </Grid>
            ))}
        </Grid>
    ), [isLoading, theme.colors.ckb.primary, homeDetail]);

    return (
        <>
            <Container maxWidth="xl">
                <Grid container md={12} xs={12}>
                    {
                        isMobile && (
                            <Box width="100%" display="flex" alignItems="center" mb={2} sx={{ borderBottom: '1px solid #000', pb: 1 }}>
                                <IconButton onClick={handleBack} sx={{ color: theme.colors.ckb.primary }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h3" sx={{ ml: 1, color: theme.colors.ckb.primary }}>
                                    Detail
                                </Typography>
                            </Box>
                        )
                    }

                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Grid container spacing={1}>
                                    <Grid item xs={7}>
                                        <Typography variant="h5" sx={{ color: theme.colors.ckb.primary }}>
                                            {dataHome?.consignee_name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography pb={2} variant="h6" gutterBottom>
                                            <div style={getActivityStyle(dataHome?.milestone)}>
                                                {dataHome?.milestone}
                                            </div>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3, pt: 0 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(shipmentNumber)}
                            </Typography>
                        </CardContent>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Booking Information
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(bookingInformation)}
                            </Typography>
                        </CardContent>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Shipment Information
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(shipmentInformation)}
                            </Typography>
                        </CardContent>
                    </Grid>

                    {/* Sender/Shipper Detail */}
                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Sender/Consignee Detail
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(senderDetails)}
                            </Typography>
                        </CardContent>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Receiver/Consignee Detail
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(receiverDetails)}
                            </Typography>
                        </CardContent>
                    </Grid>
                </Grid>

                <Grid sx={{ m: 1, pb: 2, minHeight: '300px' }}>
                    <DataTable
                        setOpen={setOpen}
                        dataGrid={homeDetailGrid}
                        setDataItem={setDataItem}
                        handleDeleteOpen={handleDeleteOpen}
                    />
                </Grid>
            </Container>
        </>
    );
}

export default SummaryDetailMobile;